import 'isomorphic-fetch';

import uniqBy from 'lodash/uniqBy';

import { useFeatureAppConfig } from '../hooks/use-feature-app-config';
import { CategoryModels } from '../types/model';
import { useFeatureServices } from '../hooks/use-feature-services';
import ErrorCodes from '../error-handling/error-codes';
import ErrorMessages from '../error-handling/error-messages';

interface FetchModelsConfigOptions {
  readonly language: string;
  readonly countryCode: string;
  readonly currency: string;
  readonly faServicesUrl: string;
  readonly mockIds?: string;
  readonly mocksBaseUrl?: string;
}

interface ErrorHandlingModel {
  errorCode: string;
  errorMessage: string;
}

export interface ModelsConfigResponse {
  errorHandling: ErrorHandlingModel[];
  fetchedModelsConfig?: CategoryModels[];
}

export async function fetchModelsConfigData(
  setModelsConfigData: React.Dispatch<
    React.SetStateAction<ModelsConfigResponse | undefined>
  >,
): Promise<any> {
  const { 'locale-service': localeService } = useFeatureServices();
  const { language, countryCode, currency } = localeService;
  const { mockIds, faServicesUrl } = useFeatureAppConfig();
  const { configs } = useFeatureServices()['service-config-provider'];

 

  let fetchedModelsConfig: CategoryModels[] | undefined;
  let errorHandling: ErrorHandlingModel[] = [];

  const faServices = faServicesUrl || configs['fa-services']?.urlOrigin;
  !faServices && console.error('No value provided for faServicesUrl');

  const fetchModelsConfigPromise = fetchModelsConfig({
    language,
    countryCode,
    currency,
    faServicesUrl,
    mockIds,
  })
    .then((result: any) => {
      if (result.errorStatus) {
        console.log('Error Fetching Models Config');
        fetchedModelsConfig = undefined;
        errorHandling = getModelsConfigError(errorHandling);
      } else {
        fetchedModelsConfig = result.data;
      }
    })
    .catch((error: Error) => {
      console.log('Error Fetching Models Config', error);
      fetchedModelsConfig = undefined;
      errorHandling = getModelsConfigError(errorHandling);
    });
  Promise.all([fetchModelsConfigPromise]).then(() => {
    if (setModelsConfigData) {
      setModelsConfigData({
        errorHandling,
        fetchedModelsConfig,
      });
    }
  });
}

async function fetchModelsConfig({
  language,
  countryCode,
  currency,
  mockIds,
  mocksBaseUrl,
  faServicesUrl,
}: FetchModelsConfigOptions): Promise<any> {
  const params = new URLSearchParams({ language, countryCode, currency });

  if (mockIds) {
    params.set('mockIds', mockIds);
  }

  if (mocksBaseUrl) {
    params.set('mocksBaseUrl', mocksBaseUrl);
  }
  params.set('useGlobalConfig', 'true');
  params.set('groupBy', 'category');
  params.set('isInventory', 'true');
  try {
    const response = await fetch(createBffUrl(faServicesUrl, params));
    const modelOverview = await response.json();

    if (response.status >= 500) {
      return errorHandler(response, true);
    }

    return errorHandler(modelOverview, false);
  } catch (error) {
    return errorHandler(error, true);
  }
}

function createBffUrl(
  baseUrl: string | undefined,
  params: URLSearchParams,
): string {
  const bffPath = baseUrl?.endsWith('/') ? 'modelsConfig' : '/modelsConfig';

  return `${baseUrl || ''}${bffPath}?${params.toString()}`;
}

const errorHandler = (obj: any, status: boolean) => {
  if (status) {
    return {
      errorStatus: true,
      data: obj,
    };
  }

  return {
    errorStatus: false,
    data: obj,
  };
};

const getModelsConfigError = (errorHandling: ErrorHandlingModel[]) =>
  uniqBy(
    [
      {
        errorCode: ErrorCodes.FetchModelOverviewError,
        errorMessage: ErrorMessages.FetchModelOverviewError,
      },
      ...errorHandling,
    ],
    'errorCode',
  );
